import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  IconHeart,
  IconPlay,
  IconSpinner,
  ResponsiveImage,
} from '../../../components';
import ListingStatus from './ListingStatus';
import ListingPromotion from './ListingPromotion';
import Overlay from './Overlay';

import css from './ManageListingCard.module.css';

const MOBILE_BREAKPOINT_PX = 768;

/**
 * The photo half of the card: image or video, the play control, the status badge,
 * the promotion label, and the in-progress / error overlays.
 *
 * Lifted out of ManageListingCard so the card is not also carrying video playback
 * and viewport state. Behaviour is unchanged — this is a move, not a redesign.
 *
 * Note the promotion label stays wired up even though paid promotions are dormant:
 * see PROMOTIONS_REVERT_NOTES.md. It is here rather than in the card body so that
 * re-enabling the feature does not mean touching the card again.
 */
const ListingCardMedia = props => {
  const {
    listing,
    title,
    firstImage,
    optimizedImages,
    variants,
    renderSizes,
    aspectWidth,
    aspectHeight,
    videoUrl,
    isAboveFold,
    isFaded,
    isDisabled,
    isSold,
    isSubscriptionExpired,
    isPendingApproval,
    isOnManageListingPage,
    hideLabel,
    showFavoriteButton,
    isFavorite,
    onToggleFavorites,
    isInProgress,
    hasError,
    errorMessage,
    children,
  } = props;

  const intl = useIntl();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePlayClick = e => {
    if (videoUrl) {
      e.preventDefault();
      e.stopPropagation();
      setIsVideoPlaying(true);
    }
  };

  return (
    <div
      className={classNames(css.clickWrapper, {
        [css.faded]: isFaded,
        [css.clickWrapperDisabled]: isDisabled,
        [css.clickWrapperSold]: isSold,
      })}
    >
      {showFavoriteButton && (
        <button
          type="button"
          aria-pressed={isFavorite}
          className={classNames(css.mobileFavoriteButton, { [css.favoritedIcon]: isFavorite })}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorites(isFavorite);
          }}
        >
          <IconHeart isFilled={isFavorite} />
        </button>
      )}

      <AspectRatioWrapper
        width={aspectWidth}
        height={aspectHeight}
        className={css.aspectRatioWrapper}
      >
        {isVideoPlaying && videoUrl ? (
          <video
            className={css.videoPlayer}
            src={videoUrl}
            autoPlay
            loop
            controls
            onEnded={() => setIsVideoPlaying(false)}
            onPause={() => setIsVideoPlaying(false)}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <ResponsiveImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
            optimizedImages={optimizedImages}
            imageIndex={0}
            {...(isAboveFold ? { fetchPriority: 'high', loading: 'eager' } : {})}
          />
        )}

        {videoUrl && !isVideoPlaying && (
          <button
            className={classNames(css.playButton, { [css.playButtonVisible]: isMobile })}
            onClick={handlePlayClick}
            aria-label={intl.formatMessage({ id: 'ListingCard.playVideo' })}
            type="button"
          >
            <IconPlay className={css.playIcon} />
          </button>
        )}

        <div className={css.imageTooltip}>
          <FormattedMessage id="ManageListingCard.imagePreviewTooltip" />
        </div>
      </AspectRatioWrapper>

      {isOnManageListingPage && (
        <ListingStatus currentListing={listing} isSubscriptionExpired={isSubscriptionExpired} />
      )}
      {!hideLabel && <ListingPromotion currentListing={listing} />}

      {isPendingApproval && (
        <Overlay
          message={intl.formatMessage({ id: 'ManageListingCard.pendingApproval' }, { listingTitle: title })}
        />
      )}

      {isInProgress ? (
        <Overlay>
          <IconSpinner />
        </Overlay>
      ) : hasError ? (
        <Overlay errorMessage={errorMessage} />
      ) : null}

      {children}
    </div>
  );
};

export default ListingCardMedia;
